import { PayChannel, type MemberProduct } from "@features/api/memberApi";
import {
  getAvailableChannels,
  type CompletePlansByType,
} from "@hooks/memberPlansData";
import {
  pickCheckoutChannels,
  resolveChannelMeta,
} from "@utils/member/checkoutChannel";

export type OnboardingPricingOffer = {
  key: keyof CompletePlansByType;
  product: MemberProduct;
  purchaseProduct: MemberProduct;
  price: number;
  currency: "USD";
  dailyPrice?: number;
  discount?: number;
  paybackYears?: string;
  trial?: { price?: number; days?: number };
};

const amount = (product?: MemberProduct) => {
  const raw =
    product?.currencies?.find((row) => row.currency === "USD")?.price ??
    product?.price;
  if (raw == null || String(raw).trim() === "") {
    return undefined;
  }
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : undefined;
};

const resolveCatalogTrialDays = (
  product: MemberProduct,
): number | undefined => {
  const periods = product.effective_period;
  if (
    product.type !== "TRIAL" ||
    !Array.isArray(periods) ||
    periods.length !== 1
  ) {
    return undefined;
  }
  const period = periods[0];
  if (
    !period ||
    period.unit?.toUpperCase() !== product.pay_cycle ||
    (period.unit !== "day" && period.unit !== "week") ||
    typeof period.count !== "number" ||
    !Number.isSafeInteger(period.count) ||
    period.count <= 0
  ) {
    return undefined;
  }
  const days = period.count * (period.unit === "week" ? 7 : 1);
  return Number.isSafeInteger(days) ? days : undefined;
};

export const resolveOnboardingTrialDays = (
  product: MemberProduct,
  channel: PayChannel,
): number | undefined => {
  if (!getAvailableChannels(product).includes(channel)) {
    return undefined;
  }
  const channelOffers = (product.payment_configs ?? [])
    .filter(
      (config) => config.status === "ACTIVE" && config.channel === channel,
    )
    .flatMap((config) => config.offers ?? []);
  // 渠道没有 offer 配置时是否回退目录天数由 CHANNEL_META 声明（PAYPAL / AIRWALLEX 回退，STRIPE 不回退），
  // 不在这里写死渠道（EDG-552：AIRWALLEX 当主渠道时原写法会把试用判为未就绪而隐藏 CTA）。
  if (
    resolveChannelMeta(channel, product)?.catalogTrialFallback &&
    channelOffers.length === 0
  ) {
    return resolveCatalogTrialDays(product);
  }
  const offers = channelOffers.filter((offer) => offer.status === "ACTIVE");
  const days = offers[0]?.trial_period_days;
  if (
    typeof days !== "number" ||
    !Number.isSafeInteger(days) ||
    days <= 0 ||
    offers.some((offer) => offer.trial_period_days !== days)
  ) {
    return undefined;
  }
  return days;
};

export const isOnboardingTrialReady = (
  product: MemberProduct,
  channel: PayChannel,
) =>
  product.type !== "TRIAL" ||
  (amount(product) != null &&
    resolveOnboardingTrialDays(product, channel) != null);

export const onboardingOfferForChannel = (
  offer: OnboardingPricingOffer,
  channel: PayChannel,
): OnboardingPricingOffer => ({
  ...offer,
  trial: offer.trial
    ? {
        ...offer.trial,
        days: resolveOnboardingTrialDays(offer.purchaseProduct, channel),
      }
    : undefined,
});

export const buildOnboardingPricingOffers = (
  plans: CompletePlansByType,
  trialList: MemberProduct[] = [],
): OnboardingPricingOffer[] => {
  const monthlyPrice = amount(plans.monthly);
  return (["yearly", "monthly", "weekly", "lifetime"] as const).flatMap(
    (key) => {
      const product = plans[key];
      const price = amount(product);
      if (
        !product?.id ||
        product.type !== "NORMAL" ||
        price == null ||
        price <= 0
      ) {
        return [];
      }
      const trialProduct =
        key === "yearly"
          ? trialList.find(
              (candidate) =>
                candidate.type === "TRIAL" &&
                candidate.status === "ACTIVE" &&
                candidate.renewal_id === product.id,
            )
          : undefined;
      // 试用天数按主渠道（后端顺序第一个）解析，与付费墙主 CTA 下单渠道一致（EDG-552，原写死 STRIPE 优先）
      const primaryChannel = trialProduct
        ? pickCheckoutChannels(trialProduct).primary
        : null;
      const divisor = {
        yearly: 365,
        monthly: 365 / 12,
        weekly: 7,
        lifetime: undefined,
      }[key];
      const discount =
        key === "yearly" && monthlyPrice
          ? Math.floor((1 - price / (monthlyPrice * 12)) * 100)
          : undefined;
      return [
        {
          key,
          product,
          purchaseProduct: trialProduct ?? product,
          price,
          currency: "USD" as const,
          dailyPrice: divisor
            ? Math.round((price / divisor + Number.EPSILON) * 100) / 100
            : undefined,
          discount: discount && discount > 0 ? discount : undefined,
          paybackYears:
            key === "lifetime" && monthlyPrice
              ? (price / (monthlyPrice * 12)).toFixed(1)
              : undefined,
          trial: trialProduct
            ? {
                price: amount(trialProduct),
                days: primaryChannel
                  ? resolveOnboardingTrialDays(trialProduct, primaryChannel)
                  : undefined,
              }
            : undefined,
        },
      ];
    },
  );
};

export const resolveOnboardingOfferSelection = (
  offers: OnboardingPricingOffer[],
  planId?: string | null,
) =>
  planId
    ? offers.find((offer) => offer.product.id === planId)
    : offers.find((offer) => offer.key === "yearly");
